import React, { useState, useEffect, useRef } from 'react';

// Define types for different ad formats
interface BaseAd {
  id: string;
}

interface CustomAd extends BaseAd {
  type: 'book' | 'product' | 'game';
  title: string;
  description: string;
  imageUrl: string;
  linkUrl: string;
  ctaText: string;
  bgColor: string;
}

interface AdSenseAd extends BaseAd {
  type: 'google-adsense';
  client: string;
  slot: string;
}

type AdUnit = CustomAd | AdSenseAd;

export const AdBanner: React.FC<{ position: 'top' | 'bottom' }> = ({ position }) => {
  const [ad, setAd] = useState<AdUnit | null>(null);
  const adSenseRef = useRef<HTMLDivElement>(null);
  const processedRef = useRef(false);
  
  // FEATURE FLAG: Set to true to enable ads
  const SHOW_ADS = false;

  // AMAZON AFFILIATE CONFIGURATION
  // TODO: Replace 'your-tag-20' with your actual Amazon Associates Store ID
  const AFFILIATE_TAG = 'your-tag-20'; 

  const ADS: AdUnit[] = [
    {
      id: 'book-attached',
      type: 'book',
      title: "Attached",
      description: "Understand attachment styles and stop chasing. The #1 relationship book.",
      // Stable Amazon Image URL
      imageUrl: "https://m.media-amazon.com/images/I/613KCs7szvL._AC_UF1000,1000_QL80_.jpg", 
      linkUrl: `https://amzn.to/49EevUf`,
      ctaText: "Read on Amazon",
      bgColor: "bg-orange-50"
    },
    {
      id: 'book-the5lovelanguages',
      type: 'book',
      title: "The 5 Love Languages",
      description: "The Secret to Love that Lasts",
      // Stable Amazon Image URL
      imageUrl: "https://m.media-amazon.com/images/I/81N7FmJhbhL._AC_UF1000,1000_QL80_.jpg", 
      linkUrl: `https://amzn.to/3LSztpo`,
      ctaText: "Read on Amazon",
      bgColor: "bg-slate-100"
    },
    {
      id: 'book-hisneedsherneeds',
      type: 'book',
      title: "His Needs, Her Needs:",
      description: "Making Romantic Love Last (How to Identify and Satisfy 10 Vital Needs in Your Marriage",
      // Stable Amazon Image URL
      imageUrl: "https://m.media-amazon.com/images/I/81aKHcmn-QL._AC_UF1000,1000_QL80_.jpg", 
      linkUrl: `https://amzn.to/45qGmF1`,
      ctaText: "Read on Amazon",
      bgColor: "bg-pink-50"
    },
    // Google AdSense Ad
    {
        id: 'google-adsense-1',
        type: 'google-adsense',
        client: 'ca-pub-3103297757054738',
        slot: '7265907926'
    }
  ];

  useEffect(() => {
    if (!SHOW_ADS) return;
    // Randomly select an ad on mount
    const randomAd = ADS[Math.floor(Math.random() * ADS.length)];
    setAd(randomAd);
  }, [SHOW_ADS]);

  useEffect(() => {
    if (!SHOW_ADS) return;
    // Trigger Adsbygoogle only if the ad type is AdSense and it hasn't been processed yet
    if (ad?.type === 'google-adsense' && adSenseRef.current && !processedRef.current) {
        try {
            processedRef.current = true;
            // @ts-ignore
            (window.adsbygoogle = window.adsbygoogle || []).push({});
        } catch (e) {
            console.error("AdSense error", e);
        }
    }
  }, [ad, SHOW_ADS]);

  if (!SHOW_ADS || !ad) return null;

  // Render AdSense Ad
  if (ad.type === 'google-adsense') {
      return (
        <div className={`w-full bg-slate-50 border-y border-slate-200 flex justify-center overflow-hidden min-h-[100px] ${position === 'bottom' ? 'mb-0 py-2' : 'mt-0 py-2'}`}>
            <div className="w-full text-center max-w-[100vw]" ref={adSenseRef}>
                <span className="text-[9px] text-slate-300 uppercase tracking-widest block mb-1">Advertisement</span>
                <ins className="adsbygoogle"
                    style={{ display: 'block' }}
                    data-ad-client={ad.client}
                    data-ad-slot={ad.slot}
                    data-ad-format="auto"
                    data-full-width-responsive="true"></ins>
            </div>
        </div>
      );
  }

  // Render Custom Ad (Books, Products, etc.)
  const customAd = ad as CustomAd;

  return (
    <div className={`w-full bg-slate-50 border-y border-slate-200 flex justify-center ${position === 'bottom' ? 'mb-0 py-2' : 'mt-0 py-2'}`}>
      <a 
        href={customAd.linkUrl} 
        target="_blank" 
        rel="noopener noreferrer"
        className={`flex items-center gap-3 px-3 py-2 ${customAd.bgColor} border border-slate-200 rounded-lg shadow-sm hover:shadow-md transition-all max-w-sm w-full mx-4 group relative overflow-hidden`}
      >
        {/* Ad Label */}
        <div className="absolute top-0 right-0 bg-slate-200 text-[8px] text-slate-500 px-1 rounded-bl">
            Sponsored
        </div>

        <div className="w-12 h-12 bg-white flex-shrink-0 overflow-hidden rounded-md shadow-sm border border-slate-100">
            <img src={customAd.imageUrl} alt={customAd.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
        </div>
        
        <div className="flex-1 text-left min-w-0 pr-1">
            <p className="text-xs font-bold text-slate-800 truncate">{customAd.title}</p>
            <p className="text-xs text-slate-600 leading-tight line-clamp-2 mt-0.5">{customAd.description}</p>
        </div>

        <div className="flex-shrink-0">
             <div className="bg-amber-400 text-slate-900 text-[10px] font-bold px-3 py-1.5 rounded-full shadow-sm whitespace-nowrap group-hover:bg-amber-500 transition-colors flex items-center gap-1">
                {customAd.ctaText}
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3">
                  <path fillRule="evenodd" d="M5 10a.75.75 0 01.75-.75h6.638L10.23 7.29a.75.75 0 111.04-1.08l3.5 3.25a.75.75 0 010 1.08l-3.5 3.25a.75.75 0 11-1.04-1.08l2.158-1.96H5.75A.75.75 0 015 10z" clipRule="evenodd" />
                </svg>
            </div>
        </div>
      </a>
    </div>
  );
};